/**
 * RAM — Change Ownership Type (Wave 10).
 *
 * POST /v3/maint/acct/change-ownership
 *
 * Ownership-type changes drive downstream IRS reporting + signature card
 * refresh, and require Compliance review. The new CallClass is selected from
 * the CoreDirectorCodes catalog (domain = 'CallClass').
 */
import { useMemo, useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import { emitDisclosureRedeliveryRequired } from '../disclosureTriggers';
import type { RamTarget } from '../types';

export interface ChangeOwnershipProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function ChangeOwnership({
  target,
  onDone,
  onCancel,
}: ChangeOwnershipProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [newCallClass, setNewCallClass] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const callClassOptions = useMemo(
    () =>
      (config?.coreDirectorCodes ?? [])
        .filter((row) => row.domain === 'CallClass' && row.isActive)
        .slice()
        .sort((a, b) => {
          const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
          const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
          if (ao !== bo) return ao - bo;
          return a.code.localeCompare(b.code);
        }),
    [config?.coreDirectorCodes],
  );

  const catalogEmpty = callClassOptions.length === 0;
  const reasonValid = reason.trim().length >= 10;
  const canSave =
    !submitting &&
    !catalogEmpty &&
    Boolean(target.acctId) &&
    Boolean(newCallClass) &&
    reasonValid;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeOwnership(b),
      {
        accountNumber: target.acctId,
        newCallClass,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    // Reg DD §1030.4 + IRS W-9 — new CallClass triggers SIG_CARD refresh + W9.
    void emitDisclosureRedeliveryRequired(client, {
      action: 'change-ownership',
      cifId: target.cifId,
      accountNumber: target.acctId,
      actorUpn: user?.upn,
      payload: { newCallClass },
    });
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Ownership change submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Change ownership type</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Ownership-type changes require Compliance review. The CallClass change
        drives downstream IRS reporting + signature card refresh.
      </Banner>

      {catalogEmpty && (
        <Banner tone="warn">
          CoreDirectorCodes catalog has no active CallClass rows — the admin
          must populate the CallClass domain (Admin → Reference → Codes).
        </Banner>
      )}

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          New CallClass
          <select
            className="ram-input"
            value={newCallClass}
            onChange={(e) => setNewCallClass(e.target.value)}
            disabled={catalogEmpty}
          >
            <option value="">— Select CallClass —</option>
            {callClassOptions.map((row) => (
              <option key={row.code} value={row.code}>
                {row.code} — {row.label}
              </option>
            ))}
          </select>
        </label>
        <label className="ram-search__label">
          Reason / Compliance review notes
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={1024}
            placeholder="Minimum 10 characters — describe the ownership change reason and any Compliance findings."
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit ownership change'}
        </Button>
      </div>
    </div>
  );
}
